import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Sparkles,
  ShieldCheck,
  Mail,
  Phone,
  Loader2,
  Lock,
  LayoutDashboard,
} from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import SeoMeta from "@/components/SeoMeta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CREATOR_TYPES = [
  "Content Creator",
  "Affiliate Marketer",
  "Local Influencer",
  "Freelancer",
  "Sales Professional",
  "Community Connector",
  "Event Promoter",
  "Online Publisher",
  "Virtual Assistant",
  "Social Media Manager",
  "Photographer / Videographer",
  "Spokesperson / Host",
  "Business Consultant",
  "Other",
];

export default function OnlyBestApplyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    city: "",
    state: "",
    creator_type: "",
    audience_size: "",
    referral_pipeline: "",
    knows_owners_count: "",
    why_join: "",
    sms_consent: false,
    agree_terms: false,
    disclose_compensation: false,
    wants_video_work: false,
    referrer_slug: "",
  });

  // Capture referrer attribution from URL (?ref=creator-slug or ?creator=slug).
  // First referrer wins — persist in localStorage for 90 days so the attribution
  // follows the applicant through the $1 apply + booking flow.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refFromUrl = (params.get("ref") || params.get("creator") || "").trim().toLowerCase();
    const stored = (() => {
      try {
        const raw = localStorage.getItem("ob_referrer");
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const now = Date.now();
        if (parsed?.slug && parsed?.exp && parsed.exp > now) return parsed.slug;
      } catch { /* ignore */ }
      return null;
    })();
    let effective = stored || refFromUrl;
    if (refFromUrl && !stored) {
      // First-referrer wins — write once
      try {
        localStorage.setItem(
          "ob_referrer",
          JSON.stringify({ slug: refFromUrl, exp: Date.now() + 90 * 24 * 60 * 60 * 1000 })
        );
      } catch { /* ignore */ }
      effective = refFromUrl;
    }
    if (effective) setForm((p) => ({ ...p, referrer_slug: effective }));
  }, [location.search]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [listingSlug, setListingSlug] = useState(null);

  // Password / dashboard creation state
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [creating, setCreating] = useState(false);
  const [accountReady, setAccountReady] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const validate = () => {
    if (!form.first_name.trim() || !form.last_name.trim())
      return "Please share your first and last name.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email))
      return "Please enter a valid email address.";
    if (form.phone.replace(/\D/g, "").length < 7)
      return "Please enter a valid phone number.";
    if (!form.agree_terms)
      return "Please agree to the OnlyBest Creator terms to submit.";
    if (!form.disclose_compensation)
      return "Please agree to the compensation-disclosure terms to submit.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/onlybest/apply`, form);
      if (r.data?.ok) {
        setSubmitted(true);
        toast.success("Application received!");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "We couldn't submit your application — please try again in a moment.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== passwordConfirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/v1/auth/register`, {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        password,
        business_name: `${form.first_name} ${form.last_name}`,
      });
      localStorage.setItem("auth_token", res.data.token);
      localStorage.setItem("auth_user", JSON.stringify(res.data.user));
      setAccountReady(true);
      toast.success("Dashboard ready — taking you in…");
      setTimeout(() => navigate("/dashboard"), 900);
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail.includes("already exists")) {
        // Account already exists for this email — try login
        try {
          const loginRes = await axios.post(`${API}/v1/auth/login`, {
            email: form.email,
            password,
          });
          localStorage.setItem("auth_token", loginRes.data.token);
          localStorage.setItem("auth_user", JSON.stringify(loginRes.data.user));
          setAccountReady(true);
          toast.success("Welcome back!");
          setTimeout(() => navigate("/dashboard"), 900);
        } catch {
          toast.error(
            "An account exists for this email but the password didn't match. Use the Dashboard Login link."
          );
        }
      } else {
        toast.error(detail || "We couldn't create your dashboard. Try again.");
      }
    } finally {
      setCreating(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#070B16] text-white" data-testid="onlybest-apply-success">
        <SeoMeta
          title="Application Received — Set Your Password | LocalBest.ai"
          description="Your OnlyBest Creator application has been received. Set your password to access your dashboard."
          canonical="https://localbest.ai/onlybest/apply"
        />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br from-[#10B981] to-[#2563EB] items-center justify-center mb-6"
            >
              <CheckCircle2 className="w-8 h-8 text-white" />
            </motion.div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-['Outfit'] tracking-tight">
              You&apos;re in, {form.first_name}.
            </h1>
            <p className="mt-4 text-base sm:text-lg text-[#94A3B8] leading-relaxed">
              Your application is logged in our AI Horse-Powered CRM and your{" "}
              <span className="text-white font-semibold">OnlyBest Creator Listing</span>{" "}
              has been drafted for you. Set your password below to open your dashboard
              and start publishing.
            </p>
          </div>

          {/* PASSWORD CREATION CARD */}
          {!accountReady && (
            <motion.form
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              onSubmit={handleCreateAccount}
              className="mt-10 rounded-3xl border border-[#F59E0B]/30 bg-gradient-to-br from-[#F59E0B]/10 via-[#0F172A] to-[#EC4899]/10 p-6 sm:p-8"
              data-testid="onlybest-set-password-form"
            >
              <div className="flex items-center gap-3 mb-5">
                <span className="inline-flex w-10 h-10 rounded-xl bg-gradient-to-br from-[#F59E0B] to-[#EC4899] items-center justify-center">
                  <Lock className="w-5 h-5 text-white" />
                </span>
                <div>
                  <h2 className="text-white font-semibold text-lg">
                    Create your dashboard password
                  </h2>
                  <p className="text-xs text-[#94A3B8]">
                    Used to sign in to your OnlyBest Creator dashboard at any time.
                  </p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="pwd" className="text-sm text-white">
                    Password (8+ chars)
                  </Label>
                  <Input
                    id="pwd"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    className="mt-1 bg-white/5 border-white/10 text-white"
                    data-testid="apply-password"
                  />
                </div>
                <div>
                  <Label htmlFor="pwd2" className="text-sm text-white">
                    Confirm password
                  </Label>
                  <Input
                    id="pwd2"
                    type="password"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    required
                    minLength={8}
                    className="mt-1 bg-white/5 border-white/10 text-white"
                    data-testid="apply-password-confirm"
                  />
                </div>
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={creating}
                className="mt-5 w-full bg-gradient-to-r from-[#F59E0B] to-[#EC4899] hover:opacity-90 text-white font-semibold rounded-full py-6 text-base shadow-lg shadow-[#F59E0B]/30"
                data-testid="apply-create-account-btn"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening your dashboard…
                  </>
                ) : (
                  <>
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Open My Creator Dashboard
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>

              <p className="mt-3 text-[11px] text-[#64748B] text-center">
                You&apos;ll land inside your dashboard with full publishing access —
                same capabilities as any Best of Best business or agent listing.
              </p>
            </motion.form>
          )}

          {accountReady && (
            <div className="mt-10 rounded-3xl border border-[#10B981]/30 bg-[#10B981]/5 p-6 text-center">
              <CheckCircle2 className="w-8 h-8 text-[#10B981] mx-auto mb-3" />
              <p className="text-white font-semibold">Dashboard ready — redirecting…</p>
            </div>
          )}

          {/* WHAT'S NEXT */}
          <div className="mt-12 grid sm:grid-cols-2 gap-4 text-left">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <Phone className="w-5 h-5 text-[#F59E0B] mb-3" />
              <h3 className="text-white font-semibold mb-1">We may call to verify</h3>
              <p className="text-sm text-[#94A3B8] leading-relaxed">
                A team member may reach out by phone to verify your identity and
                make sure you&apos;re a real local creator.
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <Mail className="w-5 h-5 text-[#00E5FF] mb-3" />
              <h3 className="text-white font-semibold mb-1">Email instructions inbound</h3>
              <p className="text-sm text-[#94A3B8] leading-relaxed">
                You&apos;ll receive an email from <span className="text-white">LocalBest.ai</span> with
                onboarding tips, your Creator agreement, and best practices.
              </p>
            </div>
          </div>

          {listingSlug && (
            <p className="mt-6 text-center text-xs text-[#64748B]">
              Your OnlyBest Creator Listing slug:{" "}
              <span className="text-white">/{listingSlug}</span> — you can rename it
              from your dashboard.
            </p>
          )}

          <p className="mt-4 text-center text-xs text-[#64748B]">
            Please add <span className="text-white">@localbest.ai</span> to your safe
            sender list so nothing lands in spam.
          </p>

          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/onlybest">
              <Button
                variant="outline"
                className="border-white/20 bg-white/5 hover:bg-white/10 text-white rounded-full px-6"
                data-testid="success-back-to-onlybest"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to OnlyBest
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070B16] text-white" data-testid="onlybest-apply-page">
      <SeoMeta
        title="OnlyBest Creators — Get Paid to Promote the Best Local Businesses"
        description="Apply to become an OnlyBest Creator. Introduce the local business owners you know, book them a strategy call with Roe Hubbard, and earn 20% of their first month plus 10% every month they stay active."
        canonical="https://localbest.ai/onlybest/apply"
        ogImage="https://localbest.ai/assets/localbest-verified-badge.png"
      />

      {/* Header */}
      <section className="relative pt-20 pb-10 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_30%_20%,rgba(220,38,38,0.10),transparent_55%),radial-gradient(circle_at_75%_60%,rgba(37,99,235,0.12),transparent_55%)]" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link
            to="/onlybest"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.3em] text-[#DC2626] hover:text-white transition mb-5"
            data-testid="back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to OnlyBest
          </Link>
          <p className="text-xs sm:text-sm font-bold tracking-[0.18em] text-[#DC2626] uppercase mb-3">
            OnlyBest Creator Application
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-['Outfit'] leading-[1.05] tracking-tight">
            Become an{" "}
            <span className="bg-gradient-to-r from-[#DC2626] via-white to-[#2563EB] bg-clip-text text-transparent">
              OnlyBest Creator
            </span>
          </h1>
          <p className="mt-4 text-base sm:text-lg text-[#94A3B8] leading-relaxed">
            Promote good. Earn more. Help local businesses win. There&apos;s no required
            monthly fee to start — once approved you&apos;ll receive your dashboard, your
            own OnlyBest Creator Listing, and your referral tracking links.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="pb-24">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <form
            onSubmit={handleSubmit}
            className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8 space-y-5"
            data-testid="onlybest-apply-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name" className="text-sm text-white">First name *</Label>
                <Input
                  id="first_name"
                  value={form.first_name}
                  onChange={(e) => set("first_name", e.target.value)}
                  required
                  className="mt-1 bg-white/5 border-white/10 text-white"
                  data-testid="apply-first-name"
                />
              </div>
              <div>
                <Label htmlFor="last_name" className="text-sm text-white">Last name *</Label>
                <Input
                  id="last_name"
                  value={form.last_name}
                  onChange={(e) => set("last_name", e.target.value)}
                  required
                  className="mt-1 bg-white/5 border-white/10 text-white"
                  data-testid="apply-last-name"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email" className="text-sm text-white">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  required
                  className="mt-1 bg-white/5 border-white/10 text-white"
                  data-testid="apply-email"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-sm text-white">Phone *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  required
                  className="mt-1 bg-white/5 border-white/10 text-white"
                  data-testid="apply-phone"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city" className="text-sm text-white">City</Label>
                <Input
                  id="city"
                  value={form.city}
                  onChange={(e) => set("city", e.target.value)}
                  className="mt-1 bg-white/5 border-white/10 text-white"
                  data-testid="apply-city"
                />
              </div>
              <div>
                <Label htmlFor="state" className="text-sm text-white">State</Label>
                <Input
                  id="state"
                  value={form.state}
                  onChange={(e) => set("state", e.target.value)}
                  className="mt-1 bg-white/5 border-white/10 text-white"
                  data-testid="apply-state"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="creator_type" className="text-sm text-white">
                What best describes you?
              </Label>
              <select
                id="creator_type"
                value={form.creator_type}
                onChange={(e) => set("creator_type", e.target.value)}
                className="mt-1 w-full bg-white/5 border border-white/10 text-white rounded-md px-3 py-2 text-sm"
                data-testid="apply-creator-type"
              >
                <option value="">Choose one…</option>
                {CREATOR_TYPES.map((c) => (
                  <option key={c} value={c} className="bg-[#0F172A]">
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="audience_size" className="text-sm text-white">
                Audience / following (optional)
              </Label>
              <Input
                id="audience_size"
                value={form.audience_size}
                onChange={(e) => set("audience_size", e.target.value)}
                placeholder="e.g. 5k Instagram + email list"
                className="mt-1 bg-white/5 border-white/10 text-white"
                data-testid="apply-audience-size"
              />
            </div>

            <div>
              <Label htmlFor="referral_pipeline" className="text-sm text-white">
                Local businesses or agents you could refer (optional)
              </Label>
              <Input
                id="referral_pipeline"
                value={form.referral_pipeline}
                onChange={(e) => set("referral_pipeline", e.target.value)}
                placeholder="e.g. roofers, realtors, local restaurants"
                className="mt-1 bg-white/5 border-white/10 text-white"
                data-testid="apply-referral-pipeline"
              />
            </div>

            <div>
              <Label htmlFor="knows_owners_count" className="text-sm text-white">
                How many business owners do you know personally?
              </Label>
              <select
                id="knows_owners_count"
                value={form.knows_owners_count}
                onChange={(e) => set("knows_owners_count", e.target.value)}
                className="mt-1 w-full bg-white/5 border border-white/10 text-white rounded-md px-3 py-2 text-sm"
                data-testid="apply-knows-owners-count"
              >
                <option value="">Choose one…</option>
                {["0-5", "6-20", "21-50", "50+"].map((o) => (
                  <option key={o} value={o} className="bg-[#0F172A]">{o}</option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="why_join" className="text-sm text-white">
                Why do you want to join? (optional)
              </Label>
              <Textarea
                id="why_join"
                rows={4}
                value={form.why_join}
                onChange={(e) => set("why_join", e.target.value)}
                placeholder="Tell us in a few sentences…"
                className="mt-1 bg-white/5 border-white/10 text-white"
                data-testid="apply-why-join"
              />
            </div>

            <div className="space-y-3 pt-2">
              <label className="flex items-start gap-3 text-sm text-[#CBD5E1] cursor-pointer">
                <Checkbox
                  checked={form.wants_video_work}
                  onCheckedChange={(v) => set("wants_video_work", Boolean(v))}
                  className="mt-0.5"
                  data-testid="apply-wants-video-work"
                />
                <span>
                  I&apos;m interested in creating video/content for businesses (paid per video).
                </span>
              </label>

              <label className="flex items-start gap-3 text-sm text-[#CBD5E1] cursor-pointer">
                <Checkbox
                  checked={form.sms_consent}
                  onCheckedChange={(v) => set("sms_consent", Boolean(v))}
                  className="mt-0.5"
                  data-testid="apply-sms-consent"
                />
                <span>
                  I agree to receive SMS messages from LocalBest.ai about my OnlyBest
                  Creator application and onboarding. Msg &amp; data rates may apply. Reply
                  STOP to opt out.
                </span>
              </label>

              <label className="flex items-start gap-3 text-sm text-[#CBD5E1] cursor-pointer">
                <Checkbox
                  checked={form.disclose_compensation}
                  onCheckedChange={(v) => set("disclose_compensation", Boolean(v))}
                  className="mt-0.5"
                  data-testid="apply-disclose-compensation"
                />
                <span>
                  I agree to disclose that I&apos;m compensated when I promote businesses, and I will not promise results, approvals, or Co-Op amounts. *
                </span>
              </label>

              <label className="flex items-start gap-3 text-sm text-[#CBD5E1] cursor-pointer">
                <Checkbox
                  checked={form.agree_terms}
                  onCheckedChange={(v) => set("agree_terms", Boolean(v))}
                  className="mt-0.5"
                  data-testid="apply-agree-terms"
                />
                <span>
                  I agree to promote only businesses I genuinely believe in, to follow
                  the OnlyBest Creator guidelines, and I understand that LocalBest.ai
                  may contact me to verify my identity before activating my account.
                  Earnings are not guaranteed and depend on my own effort and the
                  businesses I refer. *
                </span>
              </label>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              size="lg"
              className="w-full bg-gradient-to-r from-[#DC2626] to-[#2563EB] hover:opacity-90 text-white font-semibold rounded-full py-6 text-base shadow-lg shadow-[#2563EB]/30"
              data-testid="apply-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" /> Submit My Application — Join Free
                </>
              )}
            </Button>

            <p className="text-[11px] text-[#64748B] text-center flex items-center justify-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" />
              We&apos;ll never share your info. Verification call + email instructions sent
              after submission.
            </p>
          </form>

          {/* Disclosures block (compliance) */}
          <div className="mt-10 rounded-2xl border border-white/10 bg-[#0A0F1C] p-6" data-testid="apply-disclosures">
            <p className="text-xs uppercase tracking-[0.3em] text-[#64748B] mb-3">Disclosures</p>
            <ul className="space-y-2.5 text-xs text-[#94A3B8] leading-relaxed">
              <li className="flex items-start gap-2">
                <span className="text-[#64748B] mt-0.5">•</span>
                <span>Earnings depend on your effort and on businesses activating and staying active. Earnings are not guaranteed. Examples are illustrations, not typical results.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#64748B] mt-0.5">•</span>
                <span>Business Co-Op Credits are applied toward AI Horse-Powered services. They are not a loan, cash, or grant. Approval and amounts vary by business.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#64748B] mt-0.5">•</span>
                <span>OnlyBest Creators must clearly disclose that they are compensated when promoting businesses (for example #ad or &quot;I earn a commission&quot;).</span>
              </li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
